import { Add, Delete, Edit, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton, Typography } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useEffect, useState, useCallback, useMemo } from "react";
import FormHelper from "../../../../helpers/FormHelper";
import AddEditDialog from "./AddEditDialog";
import { useUserContext } from "../../../../contexts/UserContext";
import RemoveDialog from "./RemoveDialog";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "./Filters";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import Tooltip from "../../../../components/Tooltip";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";


const RoomBlocking = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { userPermissions } = useUserContext();

    const filtersDrawer = useDrawer();
    const [filters, setFilters] = useState<FormData | undefined>(undefined);

    const [data, setData] = useState<IRoomBlocking[]>([]);
    const [selectedRow, setSelectedRow] = useState<IRoomBlocking | undefined>(undefined);

    const addEditDialog = useDialog();
    const removeDialog = useDialog();


    const isValidFilters = useMemo(() => {
        if (filters === undefined) {
            return false;
        }

        const roomId = filters.get('roomId');
        const isValidRoomId = roomId && +roomId !== 0 ? true : false;

        const startDate = filters.get('startDate');
        const isValidStartDate = startDate && DateTimeHelper.IsValid(startDate.toString()) ? true : false;

        const endDate = filters.get('endDate');
        const isValidEndDate = endDate && DateTimeHelper.IsValid(endDate.toString()) ? true : false;

        return isValidRoomId || (isValidStartDate && isValidEndDate);
    }, [filters]);


    useEffect(() => {
        if (isValidFilters) {
            handleRefresh();
        } else {
            filtersDrawer.handleOpen();
        }
        // eslint-disable-next-line
    }, [filters, isValidFilters]);
    
    
    const handleRefresh = useCallback(() => {
        if (!isValidFilters) {
            filtersDrawer.handleOpen();
            return;
        }

        const formData = filters ?? FormHelper.Create(undefined);

        PartnerPmsService.ListRoomBlocking(formData)
            .then((resp) => {
                setData(resp.data.roomBlocking as IRoomBlocking[]);
            });
        
        // eslint-disable-next-line
    }, [filters, isValidFilters]);

    const handleAddEdit = useCallback((row: IRoomBlocking | undefined) => {
        setSelectedRow(row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);

    const handleRemove = useCallback((row: IRoomBlocking) => {
        setSelectedRow(row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'roomDescription', 
            headerName: translate('room'),
        }, { 
            field: 'date',  
            headerName: translate('date'),  
            renderCell: (params: any) => {
                return DateTimeHelper.Format(params.row.date, 'DD/MM/YYYY');
            }
        }, { 
            field: 'typeDescription',  
            headerName: translate('blockingType'), 
            flex: 1, 
            minWidth: 120
        }, { 
            field: 'description',  
            headerName: translate('description'), 
            flex: 1, 
            minWidth: 400
        }, { 
            field: 'createdAt',  
            headerName: translate('createdAt'), 
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                const createdAt = DateTimeHelper.Format(params.row.createdAt, 'DD/MM/YYYY');
                return <Typography className='fontsize-14 lineheight-14'>
                    {createdAt}
                    <Typography className='fontsize-12'>
                        {translate('by').toLowerCase()} 
                        {' '}
                        {params.row.createdBy}
                    </Typography> 
                </Typography>;
            }
        }, { 
            field: 'removedAt',  
            headerName: translate('removedAt'), 
            flex: 1,
            minWidth: 200,
            renderCell: (params: any) => {
                if (!params.row.removedBy) {
                    return null;
                }

                const removedAt = DateTimeHelper.Format(params.row.removedAt, 'DD/MM/YYYY');
                return <Typography className='fontsize-14 lineheight-14 color-red'>
                    {removedAt}
                    <Typography className='fontsize-12'>
                        {translate('by').toLowerCase()} 
                        {' '}
                        {params.row.removedBy}
                    </Typography> 
                </Typography>;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            userPermissions.includes(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            !params.row.administrator && userPermissions.includes(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        userPermissions.includes(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (userPermissions.includes(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (userPermissions.includes(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                        }}
                        getRowClassName={(params: any) => {
                            return params.row.removedBy ? 'error' : '';
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => setFilters(formData)}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default RoomBlocking;